import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal } from '../../components';
import { submitLearningPlacement, getAvailableDialectCountries } from '../../util/api';
import { COUNTRY_DIALECT_MAP, getCountryDialect } from '../../util/constants';
import ReactCountryFlag from 'react-country-flag';
import css from './LearningPlacementModal.module.css';

export const GlobeIcon = ({ size = 13, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PLACEMENT_MAP = {
  listening: { a: 'Pre-A1', b: 'A1', c: 'A2', d: 'B1' },
  speaking:  { a: 'Pre-A1', b: 'A1', c: 'A2', d: 'B1' },
  reading:   { a: 'Pre-A1', b: 'A1', c: 'A2', d: 'B1' },
  writing:   { a: 'Pre-A1', b: 'A1', c: 'A2', d: 'B1' },
  script:    { a: 'Stage 0', b: 'Stage 1', c: 'Stage 2', d: 'Stage 3' },
};

// Default 18-country list supported for dialect matching and onboarding
const DEFAULT_AVAILABLE_COUNTRIES = [
  'LB', 'SY', 'JO', 'PS', 'AE', 'SA', 'KW', 'QA', 'BH', 'OM',
  'YE', 'IQ', 'EG', 'SD', 'LY', 'TN', 'DZ', 'MA',
];

const LISTENING_OPTIONS = [
  {
    key: 'a', level: 'Pre-A1',
    title: 'Complete Beginner', arabicTitle: 'مبتدئ تماماً',
    desc: 'Understands very few or no spoken words yet.',
    arabicDesc: 'يفهم كلمات قليلة جداً أو لا يعرف بعد.',
  },
  {
    key: 'b', level: 'A1',
    title: 'Everyday Basics', arabicTitle: 'أساسيات يومية',
    desc: 'Understands common greetings, family names, and familiar routine phrases with repetition.',
    arabicDesc: 'يفهم التحيات اليومية والكلمات المألوفة مع التكرار.',
  },
  {
    key: 'c', level: 'A2',
    title: 'Familiar Conversations', arabicTitle: 'محادثات مألوفة',
    desc: 'Understands the main points of everyday conversations about school, family, and home.',
    arabicDesc: 'يفهم النقاط الرئيسية في المحادثات حول الأسرة والمدرسة.',
  },
  {
    key: 'd', level: 'B1',
    title: 'Confident Listener', arabicTitle: 'فهم واثق',
    desc: 'Follows clear conversation on many topics with good comprehension and minimal repetition.',
    arabicDesc: 'يتابع المحادثات في مواضيع متعددة بفهم جيد وبأقل تكرار.',
  },
];

const SPEAKING_OPTIONS = [
  {
    key: 'a', level: 'Pre-A1',
    title: 'Few or No Words', arabicTitle: 'كلمات قليلة أو لا يتحدث بعد',
    desc: 'Not speaking yet, or only knows a few isolated words (e.g. yes/no, thank you).',
    arabicDesc: 'لا يتحدث بعد أو يعرف كلمات معزولة فقط.',
  },
  {
    key: 'b', level: 'A1',
    title: 'Simple Introductions', arabicTitle: 'تقديم بسيط',
    desc: 'Can introduce themselves, answer basic personal questions, and use short phrases.',
    arabicDesc: 'يستطيع تقديم نفسه والإجابة عن أسئلة بسيطة بعبارات قصيرة.',
  },
  {
    key: 'c', level: 'A2',
    title: 'Daily Dialogues', arabicTitle: 'حوارات يومية',
    desc: 'Can participate in short routine dialogues, express basic needs, and describe daily habits.',
    arabicDesc: 'يستطيع المشاركة في حوارات روتينية والتعبير عن احتياجاته.',
  },
  {
    key: 'd', level: 'B1',
    title: 'Conversational', arabicTitle: 'محادثة متصلة',
    desc: 'Can express opinions, narrate simple stories, and maintain back-and-forth conversation.',
    arabicDesc: 'يستطيع التعبير عن آرائه وسرد قصص بسيطة والحفاظ على محادثة.',
  },
];

const READING_SCRIPT_OPTIONS = [
  {
    key: 'a', readingLevel: 'Pre-A1', scriptStage: 'Stage 0',
    title: 'New to the Alphabet', arabicTitle: 'بداية التعرف على الحروف',
    desc: 'Cannot recognize or name Arabic letters yet.',
    arabicDesc: 'لا يتعرف على الحروف العربية بعد.',
  },
  {
    key: 'b', readingLevel: 'A1', scriptStage: 'Stage 1',
    title: 'Letter Recognition & Phonics', arabicTitle: 'معرفة الحروف والتهجئة',
    desc: 'Recognizes most letters and basic vowel marks; sounds out 3-letter words.',
    arabicDesc: 'يتعرف على معظم الحروف والحركات ويهجئ كلمات بسيطة.',
  },
  {
    key: 'c', readingLevel: 'A2', scriptStage: 'Stage 2',
    title: 'Short Sentences & Stories', arabicTitle: 'جمل وقصص قصيرة',
    desc: 'Reads short vowelled sentences, signs, and basic illustrated children\'s stories with support.',
    arabicDesc: 'يقرأ جملاً قصيرة مشكولة وقصصاً مصورة بمساعدة.',
  },
  {
    key: 'd', readingLevel: 'B1', scriptStage: 'Stage 3',
    title: 'Connected Paragraphs', arabicTitle: 'نصوص وفقرات متصلة',
    desc: 'Reads longer passages with emerging ability to read unvowelled text independently.',
    arabicDesc: 'يقرأ نصوصاً أطول مع بداية قراءة نصوص غير مشكولة.',
  },
];

const WRITING_OPTIONS = [
  {
    key: 'a', level: 'Pre-A1',
    title: 'Not Writing Yet', arabicTitle: 'لا يكتب بعد',
    desc: 'Has not learned to write Arabic script yet.',
    arabicDesc: 'لم يتعلم كتابة الحروف العربية بعد.',
  },
  {
    key: 'b', level: 'A1',
    title: 'Copying Letters & Name', arabicTitle: 'نسخ الحروف والاسم',
    desc: 'Can copy individual letters and write their own name or very short familiar words.',
    arabicDesc: 'يستطيع نسخ الحروف وكتابة اسمه وكلمات بسيطة جداً.',
  },
  {
    key: 'c', level: 'A2',
    title: 'Simple Sentences', arabicTitle: 'جمل بسيطة',
    desc: 'Can write short phrases, simple sentences, and fill out basic information.',
    arabicDesc: 'يستطيع كتابة عبارات وجمل بسيطة وملء استمارة أساسية.',
  },
  {
    key: 'd', level: 'B1',
    title: 'Short Paragraphs', arabicTitle: 'فقرات ورسائل قصيرة',
    desc: 'Can write short personal messages, notes, and connected paragraphs describing events.',
    arabicDesc: 'يستطيع كتابة رسائل قصيرة وفقرات متصلة تصف أحداثاً.',
  },
];

/**
 * LearningPlacementModal
 *
 * Props:
 *   forced {boolean} — When true the modal renders as a full-screen overlay with
 *                      no close button and no ESC key dismissal. Used for the
 *                      post-registration gate for independent students.
 *
 *   All other props are identical to the previous FamilyDashboard version.
 */
const LearningPlacementModal = ({
  id = 'LearningPlacementModal',
  isOpen,
  onClose,
  onManageDisableScrolling,
  parentId,
  childId,
  learnerName = 'Learner',
  isIndependent = false,
  onSubmitPlacement,
  submitInProgress = false,
  submitError = null,
  onSuccess,
  // forced = true or isIndependent = true → mandatory questionnaire: no close button, no ESC dismiss
  forced = false,
  showCloseButton,
}) => {
  const intl = useIntl();
  const currentUser = useSelector(state => state.user?.currentUser);
  const userLangPref =
    currentUser?.attributes?.profile?.publicData?.languagePreference ||
    currentUser?.attributes?.profile?.protectedData?.languagePreference;
  const selectedLang = typeof window !== 'undefined' ? localStorage.getItem('selectedLanguage') : null;
  const isArabic = selectedLang
    ? selectedLang === 'ar'
    : (intl?.locale || '').toLowerCase().startsWith('ar') ||
      (userLangPref || '').toLowerCase().startsWith('ar');

  const [availableCountryCodes, setAvailableCountryCodes] = useState(DEFAULT_AVAILABLE_COUNTRIES);

  useEffect(() => {
    let isMounted = true;
    if (isOpen) {
      getAvailableDialectCountries()
        .then(res => {
          const list = res?.data?.data || res?.data;
          if (isMounted && Array.isArray(list) && list.length > 0) {
            setAvailableCountryCodes(list);
          }
        })
        .catch(() => {
          // silently keep fallback
        });
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Filter country options based on teacher availability (+ MSA & NOT_SURE are always available flagless options)
  const dialectOptions = Object.values(COUNTRY_DIALECT_MAP).filter(
    c => availableCountryCodes.includes(c.countryCode) || c.countryCode === 'MSA' || c.countryCode === 'NOT_SURE'
  );

  const [step, setStep] = useState(1);
  const [dialect, setDialect] = useState('Lebanese');
  const [listeningAnswer, setListeningAnswer] = useState('a');
  const [speakingAnswer, setSpeakingAnswer] = useState('a');
  const [readingScriptAnswer, setReadingScriptAnswer] = useState('a');
  const [writingAnswer, setWritingAnswer] = useState('a');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Keep selected dialect aligned if available list changes
  useEffect(() => {
    if (dialectOptions.length > 0 && !dialectOptions.some(d => d.dialectName === dialect)) {
      setDialect(dialectOptions[0].dialectName);
    }
  }, [dialectOptions, dialect]);

  const resetState = () => {
    setStep(1);
    setDialect(dialectOptions[0]?.dialectName || 'Lebanese');
    setListeningAnswer('a');
    setSpeakingAnswer('a');
    setReadingScriptAnswer('a');
    setWritingAnswer('a');
    setErrorMessage(null);
  };

  const isMandatory = forced || isIndependent;
  const shouldShowCloseButton = typeof showCloseButton === 'boolean' ? showCloseButton : !isMandatory;

  const handleClose = e => {
    if (isMandatory) {
      if (e && e.preventDefault) e.preventDefault();
      return;
    }
    resetState();
    if (onClose) onClose();
  };

  const handleNext = () => { if (step < 6) { setStep(s => s + 1); setErrorMessage(null); } };
  const handleBack = () => { if (step > 1) { setStep(s => s - 1); setErrorMessage(null); } };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const pId = parentId || childId;
      const cId = childId || parentId;
      const independent = isIndependent || pId === cId;
      const cd = getCountryDialect(dialect);
      const payload = {
        parentId: pId,
        childId: cId,
        learnerName,
        childName: learnerName,
        dialect: cd?.dialectName || dialect,
        dialectCountry: cd?.countryCode || null,
        isIndependent: independent,
        answers: {
          listening: listeningAnswer,
          speaking: speakingAnswer,
          reading: readingScriptAnswer,
          writing: writingAnswer,
          script: readingScriptAnswer,
        },
      };

      let data;
      if (onSubmitPlacement) {
        data = await onSubmitPlacement(payload);
      } else {
        const res = await submitLearningPlacement(payload);
        data = res?.data?.data || res?.data || res;
      }

      if (data === undefined || (data && data.success !== false)) {
        resetState();
        if (onSuccess) onSuccess(data);
        if (onClose) onClose();
      } else {
        setErrorMessage(data?.error || data?.message || intl.formatMessage({
          id: 'LearningPlacementModal.errorSubmit',
          defaultMessage: 'Failed to submit placement questionnaire',
        }));
      }
    } catch (err) {
      setErrorMessage(err?.message || intl.formatMessage({
        id: 'LearningPlacementModal.errorSubmit',
        defaultMessage: 'Failed to submit placement questionnaire',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const computedLevels = {
    listening: PLACEMENT_MAP.listening[listeningAnswer] || 'Pre-A1',
    speaking:  PLACEMENT_MAP.speaking[speakingAnswer]   || 'Pre-A1',
    reading:   PLACEMENT_MAP.reading[readingScriptAnswer]  || 'Pre-A1',
    writing:   PLACEMENT_MAP.writing[writingAnswer]     || 'Pre-A1',
    script:    PLACEMENT_MAP.script[readingScriptAnswer]   || 'Stage 0',
  };

  // ── Inner content (shared between both render modes) ────────────────────
  const innerContent = (
    <div
      className={`${css.modalRoot} ${isArabic ? css.rtl : ''}`}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Header & Step Indicators */}
      <div className={css.header}>
        <span className={css.stepBadge}>
          <FormattedMessage
            id="LearningPlacementModal.stepBadge"
            defaultMessage="Questionnaire · Step {step} of 6"
            values={{ step }}
          />
        </span>
        <div className={css.stepIndicators}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div
              key={i}
              className={`${css.stepBar} ${
                i < step ? css.stepBarCompleted : i === step ? css.stepBarActive : ''
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <h2 className={css.title}>
              <FormattedMessage
                id="LearningPlacementModal.step1Title"
                defaultMessage="Which Arabic would you like to speak?"
              />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage
                id="LearningPlacementModal.step1Subtitle"
                defaultMessage="Speaking and listening lessons are taught in the Arabic you choose. Reading, writing and script are always taught in Modern Standard Arabic."
              />
            </p>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className={css.title}><FormattedMessage id="LearningPlacementModal.step2Title" defaultMessage="Listening Comprehension" /></h2>
            <p className={css.subtitle}>
              {isIndependent
                ? <FormattedMessage id="LearningPlacementModal.step2SubtitleIndependent" defaultMessage="How much spoken Arabic do you understand when listening to family or native speakers?" />
                : <FormattedMessage id="LearningPlacementModal.step2SubtitleParent" defaultMessage="How much spoken Arabic does {learnerName} understand when listening to family or native speakers?" values={{ learnerName }} />}
            </p>
          </>
        )}
        {step === 3 && (
          <>
            <h2 className={css.title}><FormattedMessage id="LearningPlacementModal.step3Title" defaultMessage="Speaking & Conversation" /></h2>
            <p className={css.subtitle}>
              {isIndependent
                ? <FormattedMessage id="LearningPlacementModal.step3SubtitleIndependent" defaultMessage="How confident are you when expressing thoughts or speaking in Arabic?" />
                : <FormattedMessage id="LearningPlacementModal.step3SubtitleParent" defaultMessage="How confident is {learnerName} when expressing thoughts or speaking in Arabic?" values={{ learnerName }} />}
            </p>
          </>
        )}
        {step === 4 && (
          <>
            <h2 className={css.title}><FormattedMessage id="LearningPlacementModal.step4Title" defaultMessage="Alphabet & Reading" /></h2>
            <p className={css.subtitle}>
              {isIndependent
                ? <FormattedMessage id="LearningPlacementModal.step4SubtitleIndependent" defaultMessage="Where are you with recognizing Arabic letters, vowels, and reading words?" />
                : <FormattedMessage id="LearningPlacementModal.step4SubtitleParent" defaultMessage="Where is {learnerName} with recognizing Arabic letters, vowels, and reading words?" values={{ learnerName }} />}
            </p>
          </>
        )}
        {step === 5 && (
          <>
            <h2 className={css.title}><FormattedMessage id="LearningPlacementModal.step5Title" defaultMessage="Writing & Production" /></h2>
            <p className={css.subtitle}>
              {isIndependent
                ? <FormattedMessage id="LearningPlacementModal.step5SubtitleIndependent" defaultMessage="Can you write Arabic letters, words, or full sentences?" />
                : <FormattedMessage id="LearningPlacementModal.step5SubtitleParent" defaultMessage="Can {learnerName} write Arabic letters, words, or full sentences?" values={{ learnerName }} />}
            </p>
          </>
        )}
        {step === 6 && (
          <>
            <h2 className={css.title}><FormattedMessage id="LearningPlacementModal.step6Title" defaultMessage="Starting Points Preview" /></h2>
            <p className={css.subtitle}>
              {isIndependent
                ? <FormattedMessage id="LearningPlacementModal.step6SubtitleIndependent" defaultMessage="Here are the initial starting levels calculated from your answers:" />
                : <FormattedMessage id="LearningPlacementModal.step6SubtitleParent" defaultMessage="Here are the initial starting levels calculated from your answers for {learnerName}:" values={{ learnerName }} />}
            </p>
          </>
        )}
      </div>

      {(errorMessage || submitError) && (
        <div className={css.errorBanner}>{errorMessage || submitError}</div>
      )}

      {/* Step 1: Dialect */}
      {step === 1 && (
        <div className={css.dialectGrid}>
          {dialectOptions.map(opt => {
            const isSelected = dialect === opt.dialectName;
            const hasFlag = opt.countryCode && !opt.noFlag && opt.countryCode !== 'MSA' && opt.countryCode !== 'NOT_SURE';
            return (
              <div
                key={opt.countryCode}
                className={`${css.dialectCard} ${isSelected ? css.dialectCardSelected : ''}`}
                onClick={() => setDialect(opt.dialectName)}
              >
                <div className={css.dialectCardHeader}>
                  <div className={css.dialectTitle}>
                    {hasFlag && (
                      <ReactCountryFlag
                        countryCode={opt.countryCode}
                        svg
                        className={css.dialectCountryFlag}
                        title={opt.dialectName}
                        aria-label={opt.dialectName}
                      />
                    )}
                    <span>{isArabic ? opt.arabicDialectName : opt.dialectName}</span>
                  </div>
                  <div className={`${css.dialectIndicator} ${isSelected ? css.dialectIndicatorSelected : ''}`}>
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </div>
                <p className={css.dialectDesc}>{isArabic ? opt.arabicDesc : opt.desc}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 2: Listening */}
      {step === 2 && (
        <div className={css.optionsList}>
          {LISTENING_OPTIONS.map(opt => (
            <div key={opt.key} className={`${css.optionCard} ${listeningAnswer === opt.key ? css.optionCardSelected : ''}`} onClick={() => setListeningAnswer(opt.key)}>
              <div className={css.radioIndicator}>{listeningAnswer === opt.key && <div className={css.radioDot} />}</div>
              <div className={css.optionContent}>
                <div className={css.optionTitle}>{isArabic ? opt.arabicTitle : opt.title}</div>
                <p className={css.optionDesc}>{isArabic ? opt.arabicDesc : opt.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 3: Speaking */}
      {step === 3 && (
        <div className={css.optionsList}>
          {SPEAKING_OPTIONS.map(opt => (
            <div key={opt.key} className={`${css.optionCard} ${speakingAnswer === opt.key ? css.optionCardSelected : ''}`} onClick={() => setSpeakingAnswer(opt.key)}>
              <div className={css.radioIndicator}>{speakingAnswer === opt.key && <div className={css.radioDot} />}</div>
              <div className={css.optionContent}>
                <div className={css.optionTitle}>{isArabic ? opt.arabicTitle : opt.title}</div>
                <p className={css.optionDesc}>{isArabic ? opt.arabicDesc : opt.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 4: Reading & Script */}
      {step === 4 && (
        <div className={css.optionsList}>
          {READING_SCRIPT_OPTIONS.map(opt => (
            <div key={opt.key} className={`${css.optionCard} ${readingScriptAnswer === opt.key ? css.optionCardSelected : ''}`} onClick={() => setReadingScriptAnswer(opt.key)}>
              <div className={css.radioIndicator}>{readingScriptAnswer === opt.key && <div className={css.radioDot} />}</div>
              <div className={css.optionContent}>
                <div className={css.optionTitle}>{isArabic ? opt.arabicTitle : opt.title}</div>
                <p className={css.optionDesc}>{isArabic ? opt.arabicDesc : opt.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 5: Writing */}
      {step === 5 && (
        <div className={css.optionsList}>
          {WRITING_OPTIONS.map(opt => (
            <div key={opt.key} className={`${css.optionCard} ${writingAnswer === opt.key ? css.optionCardSelected : ''}`} onClick={() => setWritingAnswer(opt.key)}>
              <div className={css.radioIndicator}>{writingAnswer === opt.key && <div className={css.radioDot} />}</div>
              <div className={css.optionContent}>
                <div className={css.optionTitle}>{isArabic ? opt.arabicTitle : opt.title}</div>
                <p className={css.optionDesc}>{isArabic ? opt.arabicDesc : opt.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 6: Confirmation */}
      {step === 6 && (
        <div>
          <div className={css.dialectSummaryNotice}>
            <span>
              <strong><FormattedMessage id="LearningPlacementModal.chosenDialect" defaultMessage="Chosen Dialect:" /></strong>{' '}
              {(() => {
                const cd = getCountryDialect(dialect);
                if (cd) {
                  const hasFlag = cd.countryCode && !cd.noFlag && cd.countryCode !== 'MSA' && cd.countryCode !== 'NOT_SURE';
                  return (
                    <span className={css.dialectSummaryLabel}>
                      {hasFlag && (
                        <ReactCountryFlag
                          countryCode={cd.countryCode}
                          svg
                          className={css.dialectCountryFlag}
                          title={cd.dialectName}
                          aria-label={cd.dialectName}
                        />
                      )}
                      <span>{isArabic ? cd.arabicDialectName : cd.dialectName}</span>
                    </span>
                  );
                }
                return dialect;
              })()}
            </span>
            <button type="button" className={css.backBtn} style={{ padding: '4px 12px', fontSize: '13px' }} onClick={() => setStep(1)}>
              <FormattedMessage id="LearningPlacementModal.change" defaultMessage="Change" />
            </button>
          </div>
          <div className={css.summaryGrid}>
            {[
              {
                key: 'listening',
                label: intl.formatMessage({
                  id: 'LearningPlacementModal.skillListening',
                  defaultMessage: 'Listening',
                }),
                level: computedLevels.listening,
              },
              {
                key: 'speaking',
                label: intl.formatMessage({
                  id: 'LearningPlacementModal.skillSpeaking',
                  defaultMessage: 'Speaking',
                }),
                level: computedLevels.speaking,
              },
              {
                key: 'reading',
                label: intl.formatMessage({
                  id: 'LearningPlacementModal.skillReading',
                  defaultMessage: 'Reading',
                }),
                level: computedLevels.reading,
              },
              {
                key: 'writing',
                label: intl.formatMessage({
                  id: 'LearningPlacementModal.skillWriting',
                  defaultMessage: 'Writing',
                }),
                level: computedLevels.writing,
              },
              {
                key: 'script',
                label: intl.formatMessage({
                  id: 'LearningPlacementModal.skillScript',
                  defaultMessage: 'Arabic Script Mastery',
                }),
                level: computedLevels.script,
              },
            ].map(({ key, label, level }) => (
              <div key={key} className={css.summaryCard}>
                <div className={css.summarySkillName}>{label}</div>
                <div className={css.summaryLevelBadge}>{level}</div>
                <div className={css.provisionalTag}><FormattedMessage id="LearningPlacementModal.provisional" defaultMessage="Provisional" /></div>
              </div>
            ))}
          </div>
          <div className={css.reassuranceBanner}>
            <span className={css.bannerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
            <p className={css.bannerText}>
              <FormattedMessage id="LearningPlacementModal.reassurance" defaultMessage="These are starting points — your tutor will confirm or adjust them during your first lesson report." />
            </p>
          </div>
        </div>
      )}

      {/* Footer navigation */}
      <div className={css.footerButtons}>
        {step > 1
          ? <button type="button" className={css.backBtn} onClick={handleBack} disabled={submitting}>{isArabic ? 'السابق →' : '← Back'}</button>
          : <div />}
        {step < 6
          ? <button type="button" className={css.nextBtn} onClick={handleNext}>{isArabic ? '← التالي' : 'Next →'}</button>
          : (
            <button type="button" className={css.confirmBtn} onClick={handleSubmit} disabled={submitting || submitInProgress}>
              {submitting || submitInProgress
                ? intl.formatMessage({ id: 'LearningPlacementModal.savingBtn', defaultMessage: 'Saving Profile...' })
                : intl.formatMessage({ id: 'LearningPlacementModal.confirmBtn', defaultMessage: 'Confirm & Save Profile' })}
            </button>
          )}
      </div>
    </div>
  );

  // ── Standard Modal for both Independent and Parent modes with bg scroll ──
  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling || (() => {})}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
      showCloseButton={shouldShowCloseButton}
      usePortal
    >
      {innerContent}
    </Modal>
  );
};

LearningPlacementModal.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onManageDisableScrolling: PropTypes.func,
  parentId: PropTypes.string,
  childId: PropTypes.string,
  learnerName: PropTypes.string,
  isIndependent: PropTypes.bool,
  onSubmitPlacement: PropTypes.func,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.string,
  onSuccess: PropTypes.func,
  forced: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

export default LearningPlacementModal;
